import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { VoiceStageBackground } from "./voice-stage/voice-stage-utils"
import type {
  RealtimeAudioError,
  RealtimeAudioState,
  RealtimeVoiceToolApproval,
  VoiceToolProgress,
} from "../hooks/use-realtime-audio"
import type { VoiceEngine } from "../utils/voice-engine"
import type { VoiceTranscriptEntry } from "../utils/voice-transcript"

import {
  ArrowLeftRight,
  Central,
  CircleAlert,
  Loader2,
  Mic,
  MicOff,
  PhoneOff,
  RotateCcw,
  Volume2,
  Wrench,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { NodeConnector, NodePill } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"

import { resolveVoiceRingState } from "./voice-activity-ring-utils"
import { VOICE_MAX_SESSION_DURATION_MS } from "../hooks/use-realtime-audio"
import { currentTurnWork, isVoicePlumbingTool } from "../utils/voice-transcript"
import { VoiceGround } from "./voice-stage/voice-ground"
import { VoicePresence } from "./voice-stage/voice-presence"
import {
  DEFAULT_VOICE_STAGE_BACKGROUND,
  formatClock,
  isStackedLayout,
  readCaptionsPreference,
  resolvePresenceTag,
  resolveStageInk,
  VOICE_STAGE_BESIDE_MEDIA_QUERY,
  writeCaptionsPreference,
} from "./voice-stage/voice-stage-utils"
import styles from "./voice-stage/voice-stage.module.css"
import { VoiceTranscriptWall } from "./voice-stage/voice-transcript-wall"
import { VoiceWorkRow } from "./voice-stage/voice-work-row"
import { VoiceToolApprovalCard } from "./voice-tool-approval-card"

type VoiceModeOverlayProps = {
  open: boolean
  onClose: () => void
  onRetry?: () => void
  /** Present when the tenant has both engines: flips to the other one (reconnecting mid-call). */
  onSwitchEngine?: () => void
  state: RealtimeAudioState
  /** Drives the Live-specific copy in the hidden description. */
  engine?: VoiceEngine | null
  error?: RealtimeAudioError | null
  currentTool?: string | null
  toolProgress?: VoiceToolProgress | null
  analyserNode?: AnalyserNode | null
  /** Whether audio is currently being played */
  isPlayingAudio?: boolean
  voiceToolApproval?: RealtimeVoiceToolApproval | null
  voiceToolApprovalCount?: number
  isVoiceToolApprovalSubmitting?: boolean
  onVoiceToolApprovalDecision?: (approved: boolean) => void
  /** The in-session transcript from the hook. */
  transcript?: VoiceTranscriptEntry[]
  /** When the call became ready; drives the clock and the transcript stamps. */
  sessionStartedAt?: number | null
  background?: VoiceStageBackground
  /**
   * Forces the beside/below decision. The window measures itself in the
   * browser; tests and previews have no layout to measure.
   */
  stacked?: boolean
}

/** How long the closing card stays mounted for its exit motion. */
const WALL_CLOSE_MS = 250

const TAG_ICON = {
  idle: MicOff,
  connecting: Loader2,
  listening: Mic,
  thinking: Loader2,
  searching: Wrench,
  responding: Volume2,
  interrupted: Mic,
  reconnecting: Loader2,
  unavailable: CircleAlert,
} as const

function useElapsed(startedAt: number | null | undefined, running: boolean): number {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!startedAt) {
      setElapsed(0)
      return
    }
    // A call that is over keeps its last reading; nothing ticks behind a
    // closed window or under the error view.
    if (!running) return
    const tick = () => setElapsed(Date.now() - startedAt)
    tick()
    const interval = window.setInterval(tick, 1000)
    return () => window.clearInterval(interval)
  }, [running, startedAt])
  return elapsed
}

/**
 * Which engine the call runs on, as a quiet word after the clock. With both
 * engines enabled it is a control: one press hangs up and reconnects on the
 * other one, and the choice sticks to this device.
 */
function EnginePill({ engine, onSwitch }: { engine: VoiceEngine; onSwitch?: () => void }) {
  const { t } = useTranslation()
  const label = t(`voice_mode.engine.${engine}`)
  const pill = (
    <NodePill
      className={cn(styles.pill, styles.pillQuiet, styles.engine)}
      data-testid="voice-engine"
    >
      <span>{label}</span>
      {onSwitch && <ArrowLeftRight aria-hidden="true" className="node-pill__icon" />}
    </NodePill>
  )
  if (!onSwitch) return pill
  const other = engine === "live" ? "realtime" : "live"
  const switchLabel = t("voice_mode.engine.switch_to", { engine: t(`voice_mode.engine.${other}`) })
  return (
    <button
      aria-label={`${label}. ${switchLabel}`}
      className={styles.engineButton}
      title={switchLabel}
      type="button"
      onClick={onSwitch}
    >
      {pill}
    </button>
  )
}

/**
 * The voice window: one container on the brand's ground, with the presence
 * tag and its rings, the work the assistant does as a flow row, and the
 * transcript as a cream card beside the presence when there is room and
 * below it when there is not. The durable conversation still lives in the
 * chat behind it.
 */
export function VoiceModeOverlay({
  open,
  onClose,
  onRetry,
  onSwitchEngine,
  state,
  engine,
  error,
  currentTool,
  toolProgress = null,
  analyserNode,
  isPlayingAudio = false,
  voiceToolApproval,
  voiceToolApprovalCount = 0,
  isVoiceToolApprovalSubmitting = false,
  onVoiceToolApprovalDecision,
  transcript = [],
  sessionStartedAt = null,
  background = DEFAULT_VOICE_STAGE_BACKGROUND,
  stacked: stackedOverride,
}: VoiceModeOverlayProps) {
  const { t } = useTranslation()
  // The work of the turn in progress: a committed caller turn starts over.
  const workItems = currentTurnWork(transcript)
  const busy =
    toolProgress !== null ||
    state === "using_tools" ||
    workItems.some((item) => item.status === "running")
  const baseRingState = resolveVoiceRingState({ state, isPlayingAudio, error })
  // Tool work in flight keeps the tag on "Searching" for the whole stretch,
  // even while the assistant talks over it (full duplex) or its audio pauses,
  // instead of a tag that flips with every frame. The rings still follow
  // whichever voice is live.
  const ringState =
    busy &&
    (baseRingState === "speaking" || baseRingState === "listening" || baseRingState === "thinking")
      ? "tool"
      : baseRingState
  const baseTag = resolvePresenceTag(baseRingState, state)
  const tag =
    ringState === baseRingState
      ? baseTag
      : { ...resolvePresenceTag(ringState, state), voice: baseTag.voice }
  const TagIcon = TAG_ICON[tag.key]
  const elapsed = useElapsed(sessionStartedAt, open && state !== "idle")

  // ── Layout: beside when the viewport holds window + wall, below otherwise ─
  const [viewportStacked, setViewportStacked] = useState(() =>
    typeof window === "undefined" ? false : isStackedLayout(window.innerWidth)
  )
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return
    const query = window.matchMedia(VOICE_STAGE_BESIDE_MEDIA_QUERY)
    const update = () => setViewportStacked(!query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])
  const stacked = stackedOverride ?? viewportStacked

  // ── Transcript: preference per device, on for the first call ───────────
  const [captionsOpen, setCaptionsOpen] = useState(() => readCaptionsPreference())
  const [wallPhase, setWallPhase] = useState<"open" | "closing" | "closed">(() =>
    readCaptionsPreference() ? "open" : "closed"
  )
  const closeTimerRef = useRef<number | null>(null)
  useEffect(() => {
    if (captionsOpen) {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
      setWallPhase("open")
      return
    }
    setWallPhase((phase) => (phase === "open" ? "closing" : phase))
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null
      setWallPhase("closed")
    }, WALL_CLOSE_MS)
    return () => {
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
    }
  }, [captionsOpen])

  // ── Work: expanded steps; below a transcript only one of the two is open ─
  const [workOpen, setWorkOpen] = useState(false)
  const working = busy
  const currentLabel =
    currentTool && !isVoicePlumbingTool(currentTool)
      ? t("voice_mode.using_tool", { tool: currentTool.replace(/_/g, " ") })
      : undefined

  const showCaptions = useCallback(
    (next: boolean) => {
      setCaptionsOpen(next)
      writeCaptionsPreference(next)
      if (next && stacked) setWorkOpen(false)
    },
    [stacked]
  )
  const toggleWork = useCallback(() => {
    setWorkOpen((current) => {
      const next = !current
      if (next && stacked && captionsOpen) {
        setCaptionsOpen(false)
        writeCaptionsPreference(false)
      }
      return next
    })
  }, [captionsOpen, stacked])

  // Every call starts from the remembered preference and a folded work row.
  useEffect(() => {
    if (!open) return
    setWorkOpen(false)
    const remembered = readCaptionsPreference()
    setCaptionsOpen(remembered)
    setWallPhase(remembered ? "open" : "closed")
  }, [open])

  // A slow step is told on the work row's own label, not as a second pill.
  // `elapsed` ticks once a second while the call runs, which keeps it moving.
  const stalledSeconds =
    toolProgress?.stalled && elapsed >= 0
      ? Math.max(0, Math.floor((Date.now() - toolProgress.startedAt) / 1000))
      : null
  const hintText = (() => {
    if (ringState === "error") {
      return error ? t(`voice_mode.errors.${error}`) : t("voice_mode.errors.service_error")
    }
    return tag.hint ? t(`voice_mode.hints.${tag.hint}`) : null
  })()

  const tagLabel = t(`voice_mode.tags.${tag.key}`)
  const wallMounted = wallPhase !== "closed"
  const captionsLabel = t("voice_mode.transcript.title")
  const captionsAttr = captionsOpen ? "open" : "closed"

  const handle = !captionsOpen && (
    <button
      aria-expanded="false"
      className={cn(styles.hang, stacked ? styles.hangBottom : styles.hangRight)}
      data-testid="voice-transcript-toggle"
      title={t("voice_mode.transcript.show")}
      type="button"
      onClick={() => showCaptions(true)}
    >
      <NodePill className={styles.pill} connector={stacked ? "left" : "right"} kind="marker">
        <Central.ClosedCaptioning aria-hidden="true" className="node-pill__icon" />
        {captionsLabel}
      </NodePill>
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        data-captions={captionsAttr}
        data-ink={resolveStageInk(background)}
        data-ring={ringState}
        data-testid="voice-mode-overlay"
        className={cn(
          styles.stage,
          stacked && styles.stacked,
          "gap-0 rounded-none border-0 p-0 shadow-2xl outline-none",
          "md:border-foreground md:rounded-lg md:border",
          "[&>button]:hidden",
          "[&~[data-radix-dialog-overlay]]:bg-background/75 [&~[data-radix-dialog-overlay]]:backdrop-blur-md"
        )}
        onOpenAutoFocus={(event) => {
          // The window itself takes focus, never its first control. That control
          // is the engine switch, where a stray Space would hang up and reconnect,
          // or, with an approval pending, a write decision that must be an
          // explicit choice. Tab still reaches them in order.
          event.preventDefault()
          if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.focus({ preventScroll: true })
          }
        }}
        onPointerDownOutside={(event) => {
          if (state !== "idle" && state !== "connecting" && !error) event.preventDefault()
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("voice_mode.title", "Voice Mode")}</DialogTitle>
          <DialogDescription>
            {engine === "live" ? t("voice_mode.live_hint") : t("voice_mode.hint")}
          </DialogDescription>
        </DialogHeader>
        <VoiceGround background={background} />
        {/* One live region for the whole window, always mounted: the state word
            and its hint or error. The visible word sits inside a role="img",
            whose children are not exposed, so it cannot announce itself. */}
        <p
          aria-atomic="true"
          aria-live="polite"
          className="sr-only"
          data-testid="voice-mode-announcer"
          role="status"
        >
          {hintText ? `${tagLabel}. ${hintText}` : tagLabel}
        </p>
        <div className={styles.main}>
          <div className={styles.top}>
            <span className={cn(styles.hang, styles.hangLeft)} data-testid="voice-clock">
              <NodePill
                className={cn(styles.pill, styles.pillQuiet, styles.clock)}
                connector="left"
                kind="marker"
              >
                <b>{formatClock(elapsed)}</b>
                <span>/ {formatClock(VOICE_MAX_SESSION_DURATION_MS)}</span>
              </NodePill>
            </span>
            {engine && <EnginePill engine={engine} onSwitch={onSwitchEngine} />}
            {!stacked && handle}
          </div>
          <section aria-label={tagLabel} className={styles.column}>
            {voiceToolApproval && onVoiceToolApprovalDecision && (
              <VoiceToolApprovalCard
                approval={voiceToolApproval}
                position={1}
                submitting={isVoiceToolApprovalSubmitting}
                total={voiceToolApprovalCount}
                onApprove={() => onVoiceToolApprovalDecision(true)}
                onReject={() => onVoiceToolApprovalDecision(false)}
              />
            )}

            <VoicePresence
              analyserNode={analyserNode}
              label={tagLabel}
              ringState={ringState}
              tag={tag}
              icon={
                <TagIcon
                  aria-hidden="true"
                  className={cn(
                    "node-pill__icon",
                    (tag.key === "connecting" ||
                      tag.key === "reconnecting" ||
                      tag.key === "thinking") &&
                      styles.spin
                  )}
                />
              }
            />

            {hintText && (
              <>
                <NodeConnector className={styles.conn} orientation="vertical" />
                <div className={styles.hint}>
                  <NodePill
                    className={cn(styles.pill, ringState === "error" && styles.pillDestructive)}
                    data-testid="voice-mode-status"
                  >
                    <span>{hintText}</span>
                  </NodePill>
                </div>
              </>
            )}

            {!voiceToolApproval && (
              <VoiceWorkRow
                currentLabel={currentLabel}
                items={workItems}
                open={workOpen}
                stalledSeconds={stalledSeconds}
                working={working}
                onToggle={toggleWork}
              />
            )}

            {error && onRetry && (
              <Button className="mt-4 min-h-11 gap-2" type="button" onClick={onRetry}>
                <RotateCcw aria-hidden="true" className="size-4" />
                {t("voice_mode.try_again")}
              </Button>
            )}

            <DialogClose asChild>
              <button
                aria-label={t("voice_mode.hang_up")}
                className={styles.hangup}
                data-testid="voice-hang-up"
                title={t("voice_mode.hang_up")}
                type="button"
              >
                <PhoneOff aria-hidden="true" strokeWidth={2.25} />
              </button>
            </DialogClose>
          </section>
          {stacked && handle}
        </div>
        {wallMounted && (
          <VoiceTranscriptWall
            entries={transcript}
            phase={wallPhase === "closing" ? "closing" : "open"}
            reconnecting={state === "reconnecting"}
            sessionStartedAt={sessionStartedAt}
            onClose={() => showCaptions(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
